<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
  import FroalaEditor from 'froala-editor'
  import { usePlaceholderStore } from '@/shared/stores/placeholderStore'
  import { type Placeholder, EmailTextColorEnum, EmailTextFontEnum } from '@/types/generated'
  import {
    EDITOR_COLORS,
    EDITOR_FONT_FAMILIES,
    EDITOR_TEXT_SIZES
  } from '@/shared/constants/app/editor/editor'
  import { set } from '@vueuse/core'

  const FROALA_LICENCE_KEY = import.meta.env.VITE_FROALA_LICENCE_KEY

  const { placeholders, fetchPlaceholders } = usePlaceholderStore()
  const isInitialized = ref(false)
  const editor = ref<any>(null)
  const initialize = () => {
    if (placeholders.length > 0 && !isInitialized.value) {
      FroalaEditor.DefineIcon('imageIcon', {
        // todo: best to replace this with a non-hardcoded url
        SRC: 'https://jupus.s3-eu-central-1.ionoscloud.com/images/brackets.png',
        ALT: 'Platzhalter',
        template: 'image'
      })

      const mergeTagOptions = {}
      placeholders.forEach((placeholder: Placeholder) => {
        mergeTagOptions[placeholder.key] = `${placeholder.label}`
      })
      let mergeTagHTML = '<ul class="fr-dropdown-list" role="presentation">'
      placeholders.forEach((placeholder: Placeholder) => {
        if (
          placeholder.is_context_aware &&
          !props.enabledContextAwarePlaceholders.includes(placeholder.key)
        ) {
          return
        }
        const entry = `<li role="presentation"><a class="fr-command" tabindex="-1" role="option" data-cmd="placeholders" data-param1="${placeholder.key}" title="${placeholder.label}" aria-selected="false">${placeholder.html}</a></li>`
        if (!mergeTagHTML.includes(entry)) {
          mergeTagHTML += entry
        }
      })

      FroalaEditor.RegisterCommand('placeholders', {
        title: 'Platzhalter',
        type: 'dropdown',
        icon: 'imageIcon',
        focus: false,
        undo: false,
        refreshAfterCallback: true,

        // Buttons inside the dropdown
        options: mergeTagOptions,
        html: mergeTagHTML,

        callback: function (cmd, val: string) {
          const mergeTag = placeholders!.find((placeholder: Placeholder) => placeholder.key === val)
          const editorInstance = this // Get the editor instance
          editorInstance.html.insert(mergeTag.html)
          const lastInserted = editorInstance.$el.find('.deletable-container').last()[0]

          if (lastInserted) {
            const range = document.createRange()
            const selection = window.getSelection()

            // Set the range just after the newly inserted element
            range.setStartAfter(lastInserted)
            range.collapse(true)

            selection.removeAllRanges()
            selection.addRange(range)
          }
        }
      })
      isInitialized.value = true
    }
  }
  watch(
    placeholders,
    () => {
      initialize()
    },
    { deep: true }
  )

  const props = defineProps({
    modelValue: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },

    errors: {
      type: [Array, String],
      required: false,
      default: () => []
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    showCharCounter: {
      type: Boolean,
      required: false,
      default: false
    },
    showWordCounter: {
      type: Boolean,
      required: false,
      default: false
    },
    privateFileUpload: {
      type: Boolean,
      required: false,
      default: true
    },
    enablePlaceholderButton: {
      type: Boolean,
      required: false,
      default: false
    },
    enabledContextAwarePlaceholders: {
      type: Array,
      required: false,
      default: () => []
    },
    enableTextStylingButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    enableCodeViewButton: {
      type: Boolean,
      required: false,
      default: false
    },
    fontSize: {
      type: Number,
      required: false,
      default: 14
    },
    fontFamily: {
      type: String,
      required: false,
      default: EmailTextFontEnum.ARIAL_HELVETICA_SANS_SERIF
    },
    fontColor: {
      type: String,
      required: false,
      default: EmailTextColorEnum._000000
    },
    fullHeight: {
      type: Boolean,
      required: false,
      default: false
    },
    showDisabledStyling: {
      type: Boolean,
      required: false,
      default: true
    }
  })

  const emit = defineEmits(['update:modelValue'])

  const content = ref(props.modelValue)

  watchEffect(() => {
    content.value = props.modelValue
  })

  const getCookie = (name: string) => {
    let cookieValue = null
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';')
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim()
        if (cookie.substring(0, name.length + 1) === name + '=') {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
          break
        }
      }
    }
    return cookieValue
  }

  const csrfToken = getCookie('csrftoken')
  const uploadURL = '/api/v3/editor/upload'
  const uploadParams = {
    is_private: props.privateFileUpload
  }

  const insertSectionButtons = []
  if (props.enableTextStylingButtons) {
    insertSectionButtons.push('fontFamily', 'fontSize', 'textColor')
  }
  if (props.enablePlaceholderButton) {
    insertSectionButtons.push('placeholders')
  }
  if (props.enableCodeViewButton) {
    insertSectionButtons.push('html')
  }

  watchEffect(() => {
    if (editor.value && content.value) {
      nextTick(() => {
        try {
          const editorElement = editor.value.$el.querySelector('.fr-element')
          if (editorElement) {
            editorElement.scrollTop = editorElement.scrollHeight
          }
        } catch (error) {
          /* empty */
        }
      })
    }
  })

  const config = computed(() => {
    return {
      key: FROALA_LICENCE_KEY,
      attribution: false,
      placeholderText: props.placeholder,
      charCounterCount: props.showCharCounter,
      wordCounterCount: props.showWordCounter,
      fontSizeDefaultSelection: props.fontSize,
      fontFamilyDefaultSelection: props.fontFamily,
      fontColorDefaultSelection: props.fontColor,
      imageUploadURL: uploadURL,
      imageUploadParams: uploadParams,
      fileUploadURL: uploadURL,
      fileUploadParams: uploadParams,
      toolbarInline: false,
      quickInsertTags: [],
      fontSize: EDITOR_TEXT_SIZES,
      listAdvancedTypes: false,
      language: 'de',
      requestHeaders: {
        'X-CSRFToken': csrfToken
      },
      events: {
        initialized: function (this: any) {
          editor.value = this
          handleEditState()
        },
        contentChanged: function () {
          emit('update:modelValue', content.value)
          if (this.$el) {
            try {
              const editorElement = this.$el.querySelector('.fr-element')
              if (editorElement) {
                editorElement.scrollTop = editorElement.scrollHeight
              }
            } catch (error) {
              /* empty */
            }
          }
        }
      },
      colorsBackground: EDITOR_COLORS,
      colorsText: EDITOR_COLORS,
      fontFamily: EDITOR_FONT_FAMILIES,
      toolbarButtons: {
        moreText: {
          buttons: ['bold', 'italic', 'underline', 'formatUL', 'insertLink', 'insertImage'],
          align: 'left',
          buttonsVisible: 6
        },
        insertSection: {
          buttons: insertSectionButtons,
          align: 'right',
          buttonsVisible: 5
        }
      }
    }
  })

  const handleEditState = () => {
    const editorInstance = editor.value

    if (!editorInstance?.edit) {
      console.warn('Editor instance not properly initialized290')
      return
    }

    try {
      if (props.disabled) {
        editorInstance.edit.off()
      } else {
        editorInstance.edit.on()
      }
    } catch (error) {
      console.error('Error toggling editor state:', error)
    }
  }

  watch(
    () => props.disabled,
    (newValue) => {
      if (editor.value?._editor) {
        if (newValue === false) {
          editor.value._editor.edit.on()
        }
      }
    }
  )

  const editorStyles = computed(() => ({
    '--editor-text-color': props.fontColor,
    opacity: props.disabled && props.showDisabledStyling ? '0.7' : '1',
    backgroundColor: props.disabled && props.showDisabledStyling ? '#f3f4f6' : 'transparent',
    cursor: props.disabled ? 'not-allowed' : 'auto'
  }))
  onMounted(() => {
    if (placeholders.length == 0) {
      fetchPlaceholders()
    } else {
      initialize()
    }
  })
  onBeforeUnmount(() => {})
</script>

<template>
  <div
    :style="editorStyles"
    :class="[
      {
        'has-errors': errors.length > 0,
        'full-height': props.fullHeight,
        'editor-disabled': props.disabled && props.showDisabledStyling
      }
    ]"
    class="default-styled-container default-text-color"
  >
    <froala
      ref="editor"
      v-if="placeholders.length > 0 && isInitialized"
      v-model:value="content"
      :tag="'textarea'"
      :config="config"
    ></froala>
    <p
      v-for="error in errors"
      :key="error"
      class="mt-1 text-xs text-red-600"
    >
      <!-- # todo: this is a hack to support placeholders html in error messages. This should be replaced with a proper solution. -->
      <span v-html="error"></span>
    </p>
  </div>
</template>
<!--//.fr-toolbar {-->
<!--  //  border-radius: 4px !important;-->
<!--  //}-->
<style>
  .ph {
    box-sizing: border-box;
    --tw-text-opacity: 1 !important;
    font-weight: 500 !important;
    font-size: 0.75rem !important;
    line-height: 1rem !important;
    padding-top: 0.125rem !important;
    padding-bottom: 0.125rem !important;
    padding-left: 0.625rem !important;
    padding-right: 0.625rem !important;
    --tw-bg-opacity: 1 !important;
    --tw-border-opacity: 1 !important;

    border-width: 1px !important;
    border-radius: 0.1rem !important;
    margin-inline-end: 0.1rem !important;
    border-radius: 4px !important;
    //margin-right: 0.5rem !important;
    border: 1px solid rgba(98, 105, 118, 0.16) !important;
  }

  .ph.purple {
    border-color: rgb(192 132 252 / 1) !important;
    background-color: rgb(243 232 255 / 1) !important;
    color: rgb(107 33 168 / 1) !important;
  }

  .ph.indigo {
    background-color: rgb(224 231 255 / 1) !important;
    border-color: rgb(129 140 248 / 1) !important;
    color: rgb(55 48 163 / 1) !important;
  }

  .ph.pink {
    border-color: rgb(244 114 182 / 1) !important;
    background-color: rgb(252 231 243 / 1) !important;
    color: rgb(157 23 77 / 1) !important;
  }

  .ph.yellow {
    background-color: rgb(254 249 195 / 1) !important;
    border-color: rgb(253 224 71 / 1) !important;
    color: rgb(133 77 14 / 1) !important;
  }

  .ph.red {
    color: rgb(153 27 27 / 1) !important;
    background-color: rgb(254 226 226 / 1) !important;
    border-color: rgb(248 113 113 / 1) !important;
  }

  .ph.green {
    color: rgb(22 101 52 / 1) !important;
    background-color: rgb(220 252 231 / 1) !important;
    border-color: rgb(74 222 128 / 1) !important;
  }

  .default-styled-container ul {
    list-style-type: disc; /* Or another style like 'circle', 'square' */
    padding-left: 1.5rem; /* Ensure proper indentation for list */
  }

  .default-styled-container ul li {
    list-style-position: outside; /* Options: 'inside', 'outside' (outside is default) */
  }

  .default-styled-container p {
    margin-bottom: 0.6rem;
  }

  .has-errors .fr-toolbar,
  .has-errors .fr-wrapper,
  .has-errors .fr-second-toolbar {
    border-color: rgb(220 38 38) !important;
  }

  .default-text-color .fr-view {
    color: var(--editor-text-color) !important;
  }

  .fr-box.fr-basic .fr-element.fr-view {
    color: var(--editor-text-color) !important;
  }

  .editor-disabled .fr-toolbar,
  .editor-disabled .fr-wrapper,
  .editor-disabled .fr-second-toolbar {
    background-color: #f9f9f9 !important;
    border-color: #e5e7eb !important;
  }

  .editor-disabled .fr-wrapper {
    pointer-events: none;
  }

  .fr-element {
    scroll-behavior: smooth !important;
  }

  /* Ensure the editor has a fixed height to enable scrolling */
  .fr-box.fr-basic .fr-element {
    height: calc(100vh - 350px) !important;
    overflow-y: auto !important;
  }
</style>
